import StandardBorderedContainer from "../../Containers/StandardBorderedContainer";
import { Colors } from "../../../utils/colors";
import { Icon } from "@iconify/react";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Event, ProfilePageSection } from "@markit/common.types";
import {
  isExternalEventSpecificLink,
  isExternalGenericLink,
  isProductSpecificType,
} from "@markit/common.utils";
import { useNavigate } from "../../../hooks/useNavigate";
import { DataLoaders } from "../../../redux/slices/dataSlice";
import { DropdownMenu, DropdownMenuItem } from "../../DropdownMenu";
import CreateFormAndTrackingLinkModal from "../../CreatorDashboard/FormSubmissions/CreateFormAndTrackingLinkModal";
import ConfirmDeleteModal from "../../Containers/ConfirmPopups/ConfirmDeleteModal";
import { ProfilePageSections } from "../../ProfilePage/ProfilePageFull";

type ProfileSectionCardItemProps = {
  section: string;
  renderEventItem: ReactNode;
  foundEvent: Event | undefined;
  removeOnPress: () => void;
};

// Displays the profile page section in creator mode
const ProfileSectionCardItem = (props: ProfileSectionCardItemProps) => {
  const { section, renderEventItem, foundEvent, removeOnPress } = props;
  const [formOrLinkVisible, setFormOrLinkVisible] = useState(false);
  const [removeSectionModal, setRemoveSectionModal] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const sectionDetails = useMemo(() => {
    switch (section) {
      case ProfilePageSection.LINKS_SECTION:
        return {
          icon: (
            <Icon
              icon="mdi:link-variant"
              height={14}
              style={{ color: Colors.BLACK }}
            />
          ),
          subtext: "All of your public links in one place",
        };
      case ProfilePageSection.POSTS_SECTION:
        return {
          icon: (
            <Icon
              icon="ion:newspaper"
              height={14}
              style={{ color: Colors.BLACK }}
            />
          ),
          subtext: "All of your public blog posts in one place",
        };
      case ProfilePageSection.EVENTS_CALENDAR:
        return {
          icon: (
            <Icon
              icon="ion:calendar"
              height={14}
              style={{ color: Colors.BLACK }}
            />
          ),
          subtext: "Displays a calendar for all of your public events",
        };
      case ProfilePageSection.PRODUCTS_SECTION:
        return {
          icon: (
            <Icon
              icon="ion:bag-handle"
              height={14}
              style={{ color: Colors.BLACK }}
            />
          ),
          subtext: "All of your public products in one place",
        };
      case ProfilePageSection.FORMS_SECTION:
        return {
          icon: (
            <Icon
              icon="ion:document-text"
              height={14}
              style={{ color: Colors.BLACK }}
            />
          ),
          subtext: "All of your public sign up forms in one place",
        };
      default:
        return {
          icon: null,
          subtext:
            "This item was deleted and not visible on your profile. Remove this item.",
        };
    }
  }, [section]);

  // Opens the item's builder: the link modal for generic links, otherwise the edit screen
  const editOnPress = useCallback(() => {
    if (!foundEvent) {
      return;
    }
    if (isExternalGenericLink(foundEvent.eventType)) {
      setFormOrLinkVisible(true);
    } else if (isProductSpecificType(foundEvent.eventType)) {
      dispatch(DataLoaders.event(foundEvent.id));
      const formattedEventType = foundEvent.eventType
        .toLowerCase()
        .replace(/\s/g, "-");
      navigate(`/e/${foundEvent.id}/edit-${formattedEventType}`, {
        state: { eventId: foundEvent.id },
      });
    } else if (isExternalEventSpecificLink(foundEvent.eventType)) {
      navigate(`/e/${foundEvent.id}/editLink`, {
        state: { confirmState: true, event: foundEvent },
      });
    } else {
      dispatch(DataLoaders.event(foundEvent.id));
      navigate(`/e/${foundEvent.id}/edit`, {
        state: { editEvent: foundEvent },
      });
    }
  }, [dispatch, foundEvent, navigate]);

  const dropdownItems: DropdownMenuItem[] = useMemo(() => {
    const menuItems: DropdownMenuItem[] = [
      {
        title: "Remove",
        key: "remove_item",
        icon: "ion:trash",
        onPress: () => setRemoveSectionModal(true),
        isDestructive: true,
      },
    ];

    if (foundEvent) {
      menuItems.unshift({
        title: "Edit",
        key: "edit_item",
        icon: "ion:create",
        onPress: editOnPress,
      });
    }

    return menuItems;
  }, [editOnPress, foundEvent]);

  return (
    <>
      <div className="AlignedRow" style={{ gap: 14, flex: 1 }}>
        {foundEvent ? (
          <div style={{ flex: 1 }}>{renderEventItem}</div>
        ) : (
          <StandardBorderedContainer
            containerStyles={{
              flex: 1,
              padding: 14,
              backgroundColor: Colors.WHITE,
              border: `0.5px solid ${Colors.GRAY2}`,
            }}
          >
            <div className="ColumnNormal" style={{ gap: 4 }}>
              <div className="AlignedRow" style={{ gap: 7 }}>
                {sectionDetails.icon}
                <span className="bodyMedium">
                  {ProfilePageSections.includes(section as ProfilePageSection)
                    ? section
                    : "Deleted Item"}
                </span>
              </div>
              <span className="smallBodySubtext">{sectionDetails.subtext}</span>
            </div>
          </StandardBorderedContainer>
        )}
        <DropdownMenu
          dropdownItems={dropdownItems}
          trigger={["click"]}
          placement="bottomRight"
          altWidth={150}
        >
          <Icon
            icon="ion:ellipsis-horizontal"
            height={19}
            style={{ color: Colors.BLACK }}
          />
        </DropdownMenu>
      </div>
      <ConfirmDeleteModal
        heading={"Remove this section?"}
        subtext="You can re-add this section at any time."
        deleteButtonText="Remove"
        hideModal={!removeSectionModal}
        setIsVisible={setRemoveSectionModal}
        deleteOnPress={removeOnPress}
      />
      {formOrLinkVisible && foundEvent ? (
        <CreateFormAndTrackingLinkModal
          isVisible={formOrLinkVisible}
          setIsVisible={setFormOrLinkVisible}
          existingForm={foundEvent}
          eventType={foundEvent.eventType}
        />
      ) : null}
    </>
  );
};

export default ProfileSectionCardItem;
